import { useState } from 'react';
import {
    Button,
    ConfirmationModal,
    VerticalContainer,
    toast,
} from '@/components';
import { format, R } from '@/features/profile/resources/ProfileResources.resx';
import { InsurancePartnerBenefitsCard } from './InsurancePartnerBenefitsCard';
import { PfaProgressCardView } from './PfaProgressCardView';
import {
    removeHouseholdAccess,
    cancelInsurancePartnerEnrollment,
} from '@/features/profile/widgets/insurance-partner-details/api';
import { useMutation } from '@tanstack/react-query';
import { CompanyAccessInfo } from '@/shared/interfaces';
import {
    InsurancePartnerBenefit,
    PfaProgressCard,
    ProfilePaths,
} from '@/features/profile/model/types';

type InsurancePartnerDetailsProps = {
    companyAccessInfo: CompanyAccessInfo;
    benefits: InsurancePartnerBenefit[];
    pfaProgressCards: PfaProgressCard[];
};

export const InsurancePartnerDetails = ({
    companyAccessInfo,
    benefits,
    pfaProgressCards,
}: InsurancePartnerDetailsProps) => {
    const companyName = companyAccessInfo.Company?.CompanyName ?? '';
    const hasAccessThroughHouseholdMember =
        companyAccessInfo.HasAccessThroughHouseholdMember;
    const accessProvidingHouseholdMemberName =
        companyAccessInfo.AccessProvidingHouseholdMemberName;
    const identifierTerm =
        companyAccessInfo.InsurancePolicyProvider?.IdentifierTerm;
    const policyNumber = companyAccessInfo.PolicyNumber;
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);
    const removeAccessTitle = hasAccessThroughHouseholdMember
        ? R.RemoveHouseholdAccessTitle
        : R.RemoveInsurancePartnerTitle;
    const removeAccessDescription = format(
        hasAccessThroughHouseholdMember
            ? R.RemoveHouseholdAccessDescription
            : R.RemoveInsurancePartnerDescription,
        companyName,
    );

    const removeHouseholdAccessMutation = useMutation({
        mutationFn: removeHouseholdAccess,
        onSuccess: () => {
            window.location.assign('/profile');
        },
        onError: () => {
            toast.addToast({
                type: 'error',
                message: R.RemoveAccessError,
            });
        },
    });

    const cancelInsurancePartnerEnrollmentMutation = useMutation({
        mutationFn: cancelInsurancePartnerEnrollment,
        onSuccess: () => {
            window.location.assign('/profile/access');
        },
        onError: () => {
            toast.addToast({
                type: 'error',
                message: R.RemoveAccessError,
            });
        },
    });

    const removeAccess = async () => {
        setIsRemoving(true);
        if (hasAccessThroughHouseholdMember) {
            removeHouseholdAccessMutation.mutate();
        } else {
            cancelInsurancePartnerEnrollmentMutation.mutate();
        }
        setIsRemoving(false);
    };

    return (
        <>
            <VerticalContainer spacing="lg" className="min-h-full">
                <VerticalContainer spacing="sm">
                    <h2
                        id={`heading-${ProfilePaths.InsurancePartnerDetails}`}
                        className="hidden md:block"
                    >
                        {companyName}
                    </h2>
                    <div>
                        {format(
                            R.ProfileInsuranceYouHaveAccessVia,
                            companyName,
                        )}
                    </div>
                    <div className="font-bold">
                        {R.ChangeInsuranceProviderInfo}
                    </div>

                    {accessProvidingHouseholdMemberName && (
                        <div>
                            {format(
                                R.ProfileInsuranceYouHaveAccessViaHouseholdMember,
                                accessProvidingHouseholdMemberName,
                            )}
                        </div>
                    )}

                    {companyAccessInfo.ShouldShowPolicyNumber &&
                        identifierTerm &&
                        policyNumber && (
                            <div>
                                {format(
                                    R.ProfileInsuranceYourPolicyNumberIs,
                                    identifierTerm,
                                    policyNumber,
                                )}
                            </div>
                        )}
                </VerticalContainer>

                <div className="flex flex-wrap gap-4">
                    {benefits.map((benefit) => (
                        <InsurancePartnerBenefitsCard
                            key={benefit.caregiverName}
                            benefit={benefit}
                        />
                    ))}
                    {pfaProgressCards.map((progressCard, index) => (
                        <PfaProgressCardView
                            key={`${progressCard.CaregiverName}-${index}`}
                            progressCard={progressCard}
                        />
                    ))}
                </div>

                <Button
                    variant="danger"
                    className="mt-auto self-center md:self-end"
                    onClick={() => setShowRemoveModal(true)}
                >
                    {removeAccessTitle}
                </Button>
            </VerticalContainer>

            {showRemoveModal && (
                <ConfirmationModal
                    title={removeAccessTitle}
                    message={removeAccessDescription}
                    confirmButtonText={R.Remove}
                    confirmButtonClick={removeAccess}
                    cancelButtonText={R.Keep}
                    onClose={() => setShowRemoveModal(false)}
                    confirmIsProcessing={isRemoving}
                />
            )}
        </>
    );
};
